import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Banknote, Copy, CreditCard, FileText, Loader2, Paperclip, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AmountInput } from './ui/amount-input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { copyText } from '../lib/clipboard';
import { Trans, useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS = {
  pending_match: { labelKey: 'status.pendingMatch', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  awaiting_lender: { labelKey: 'status.awaitingLender', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  confirmed: { labelKey: 'status.confirmed', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { labelKey: 'status.rejected', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { labelKey: 'status.cancelled', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
};
// Brand names stay as is; 'other' (and unknown types) fall back to t('cardTypeOther')
const CARD_TYPES = { uzcard: 'Uzcard', humo: 'Humo', visa: 'Visa', mastercard: 'Mastercard' };
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 5 * 1024 * 1024;

const fmtAmount = (v, cur) => (cur === 'USD'
  ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : `${Math.round(v).toLocaleString('uz-UZ')} UZS`);
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('uz-UZ', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '—');
const groupCard = (n) => (n || '').replace(/(\d{4})(?=\d)/g, '$1 ');
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

const tooBig = (file, t) => {
  if (file && file.size > MAX_BYTES) {
    toast.error(t('toast.receiptTooBig'));
    return true;
  }
  return false;
};

/**
 * The borrower of a debt shared between two FinOps users reports a payment:
 * amount, the lender's card (or cash / another way) and the receipt. Same rules
 * as paying from the Telegram bot: with a receipt and a matching bank
 * notification it is confirmed automatically, otherwise the lender confirms.
 */
export function BorrowerPaymentDialog({ debt, open, onClose, onDone }) {
  const { t } = useTranslation('borrowerPayment');
  const [options, setOptions] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(null); // card id | 'cash' | 'other'
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOptions(null); setFile(null); setMethod(null);
    axios.get(`${API_URL}/api/debts/${debt.id}/pay-options`, { withCredentials: true })
      .then((res) => {
        setOptions(res.data);
        setAmount(String(res.data.remaining));
        setMethod(res.data.cards[0]?.id ?? 'cash');
      })
      .catch((err) => { toast.error(errorText(err, t('toast.optionsFailed'))); onClose(); });
  }, [open, debt.id, onClose, t]);

  const submit = async () => {
    const value = parseFloat(amount);
    if (!(value > 0) || value > options.remaining + 0.01) {
      toast.error(t('validation.amountRange', { remaining: fmtAmount(options.remaining, options.currency) }));
      return;
    }
    const form = new FormData();
    form.append('amount', String(value));
    form.append('method', typeof method === 'number' ? 'card' : method);
    if (typeof method === 'number') form.append('card_id', String(method));
    form.append('shown_at', options.shown_at);
    if (file && method !== 'cash') form.append('receipt', file);
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/debts/${debt.id}/payment-claims/report`, form, { withCredentials: true });
      toast.success(res.data.message || t('toast.reported'));
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('toast.reportFailed')));
    } finally {
      setSaving(false);
    }
  };

  const isCard = typeof method === 'number';
  const card = isCard ? options?.cards.find((c) => c.id === method) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
        </DialogHeader>
        {!options ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4 text-sm">
            <p className="text-slate-600 dark:text-slate-300">
              <Trans t={t} i18nKey="dialog.lenderLine" values={{ name: options.lender_name, remaining: fmtAmount(options.remaining, options.currency) }} components={{ b: <b /> }} />
            </p>
            <div>
              <Label>{t('common:fields.amount')}</Label>
              <AmountInput value={amount} onChange={setAmount} className="mt-1" />
              <div className="flex gap-1.5 mt-1.5">
                <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs"
                  onClick={() => setAmount(String(options.remaining))}>{t('dialog.full')}</Button>
                <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs"
                  onClick={() => setAmount(String(options.currency === 'UZS'
                    ? Math.round(options.remaining / 2) : Math.round(options.remaining * 50) / 100))}>{t('dialog.half')}</Button>
              </div>
            </div>
            <div>
              <Label>{t('dialog.howPaid')}</Label>
              <div className="mt-1 space-y-1.5">
                {options.cards.map((c) => (
                  <button key={c.id} type="button" onClick={() => setMethod(c.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 ${method === c.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-slate-500" />
                      <span className="font-medium">{CARD_TYPES[c.card_type] || t('cardTypeOther')}</span>
                      {c.label && <span className="text-slate-500">· {c.label}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 font-mono">
                      {groupCard(c.card_number)}
                      <Copy className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (await copyText(c.card_number)) toast.success(t('toast.cardCopied'));
                          else toast.error(t('toast.copyFailed'));
                        }} />
                    </div>
                    {c.holder_name && <div className="text-xs text-slate-500">{t('dialog.holder', { name: c.holder_name })}</div>}
                  </button>
                ))}
                <button type="button" onClick={() => setMethod('cash')}
                  className={`w-full text-left rounded-lg border px-3 py-2 flex items-center gap-2 ${method === 'cash'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                  <Banknote className="h-4 w-4 text-slate-500" /> {t('dialog.methodCash')}
                </button>
                <button type="button" onClick={() => setMethod('other')}
                  className={`w-full text-left rounded-lg border px-3 py-2 ${method === 'other'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                  {t('dialog.methodOther')}
                </button>
              </div>
            </div>
            {method !== 'cash' && (
              <div>
                <Label>{t('dialog.receipt')}</Label>
                <label className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                  <Upload className="h-4 w-4 text-slate-500" />
                  <span className="truncate">{file ? file.name : t('dialog.chooseFile')}</span>
                  <input type="file" accept={ACCEPT} className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f && !tooBig(f, t)) setFile(f); }} />
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  {isCard
                    ? t('dialog.cardHint', { card: card ? `•••• ${card.last4}` : t('cardTypeOther') })
                    : t('dialog.manualHint')}
                </p>
              </div>
            )}
            {method === 'cash' && (
              <p className="text-xs text-slate-500">{t('dialog.cashHint')}</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
          <Button onClick={submit} disabled={!options || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The borrower's own payment reports on a shared debt: status, receipts, adding a receipt. */
export function BorrowerPaymentClaims({ debt, refreshKey }) {
  const { t } = useTranslation('borrowerPayment');
  const [claims, setClaims] = useState(null);
  const [uploading, setUploading] = useState(null);
  const inputs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/debts/${debt.id}/my-payment-claims`, { withCredentials: true });
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClaims([]);
    }
  }, [debt.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const addReceipt = async (claimId, file) => {
    if (!file || tooBig(file, t)) return;
    const form = new FormData();
    form.append('receipt', file);
    setUploading(claimId);
    try {
      const res = await axios.post(`${API_URL}/api/debts/${debt.id}/my-payment-claims/${claimId}/receipts`, form, { withCredentials: true });
      toast.success(res.data.message || t('toast.receiptAdded'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('toast.receiptFailed')));
    } finally {
      setUploading(null);
    }
  };

  if (claims === null) {
    return <div className="mt-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  }
  if (!claims.length) {
    return <p className="mt-3 text-xs text-slate-500">{t('claims.empty')}</p>;
  }
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2 text-xs">
      <p className="font-medium text-slate-700 dark:text-slate-200">{t('claims.title')}</p>
      {claims.map((c) => {
        const st = STATUS[c.status] || { cls: 'bg-slate-100 text-slate-600' };
        const open = ['pending_match', 'awaiting_lender'].includes(c.status);
        return (
          <div key={c.id} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span>
                <b>{fmtAmount(c.amount, c.currency)}</b>
                {' · '}{c.method === 'cash' ? t('claims.cash') : c.card_last4 ? `•••• ${c.card_last4}` : t('claims.other')}
                {' · '}{fmtDateTime(c.created_at)}
              </span>
              <span className={`px-2 py-0.5 rounded-full ${st.cls}`}>{st.labelKey ? t(st.labelKey) : c.status}</span>
            </div>
            {c.status === 'confirmed' && c.received_amount != null && Math.abs(c.received_amount - c.amount) > 0.01 && (
              <p className="mt-1 text-slate-500">{t('claims.booked', { amount: fmtAmount(c.received_amount, c.currency) })}</p>
            )}
            {c.note && !open && <p className="mt-1 text-slate-500">{c.note}</p>}
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              {c.receipts.map((r, n) => (
                <a key={r.id} target="_blank" rel="noopener noreferrer"
                  href={`${API_URL}/api/debts/${debt.id}/my-payment-claims/${c.id}/receipts/${r.id}`}
                  className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline">
                  <FileText className="h-3.5 w-3.5" /> {c.receipts.length > 1 ? t('claims.receiptN', { n: n + 1 }) : t('claims.viewReceipt')}
                </a>
              ))}
              {open && c.method !== 'cash' && (
                <>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={uploading === c.id}
                    onClick={() => inputs.current[c.id]?.click()}>
                    {uploading === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3 mr-1" />}
                    {t('claims.addReceipt')}
                  </Button>
                  <input type="file" accept={ACCEPT} className="hidden" ref={(el) => { inputs.current[c.id] = el; }}
                    onChange={(e) => { addReceipt(c.id, e.target.files?.[0]); e.target.value = ''; }} />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
